import { Link, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { initialsValue } from "../../../../helpers";
import Image from "../../../elements/Image";
import Button from "../../../form/Button";
import RatingStars from "../../../common/RatingStars";
import { t } from "i18next";

const PromoterDashboard = () => {
  const { promoter, selectedLanguage } = useOutletContext();
  const [copyStatus, setCopyStatus] = useState("copy");

  // Add defensive checks for promoter data
  if (!promoter || !promoter.data) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white">
        <div className="text-center py-8">
          <p className="text-gray-500">{t("Loading promoter data")}...</p>
        </div>
      </div>
    );
  }

  const {
    eventData = {
      ...promoter?.data?.eventData,
      artistData: promoter?.data?.eventData?.artistData,
    },
    organizationData = {},
    info = {},
    uid = "",
  } = promoter.data || {};

  // Helper function for language detection
  const lngDetect = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj.en || obj.es || "";
  };

  // Format dates using basic date formatting
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatDateRange = () => {
    if (!eventData?.startDate || !eventData?.endDate) return "";
    const start = new Date(eventData.startDate);
    const end = new Date(eventData.endDate);
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    const startDay = start.getDate();
    const endDay = end.getDate();
    const month = months[start.getMonth()];
    const year = start.getFullYear();

    // Handle same day events
    if (startDay === endDay) {
      return `${startDay} ${month} ${year}`;
    }

    return `${startDay} - ${endDay} ${month} ${year}`;
  };

  // Get promoter link
  const getPromoterLink = () => {
    return `${window.location.origin}/${selectedLanguage}/event-details/${eventData?.uid}?referral_code=${uid}`;
  };

  const copyPromoterLink = async () => {
    const link = getPromoterLink();
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("copied");

      // Reset button text after 2 seconds
      setTimeout(() => {
        setCopyStatus("copy");
      }, 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.body.removeChild(textArea);

      setCopyStatus("copied");

      // Reset button text after 2 seconds
      setTimeout(() => {
        setCopyStatus("copy");
      }, 2000);
    }
  };

  return (
    <div className=" p-6 bg-white relative rounded-xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="capitalize text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {lngDetect(eventData?.title) || t("Event Title")} -{" "}
          {formatDate(eventData?.startDate)}
        </h1>
      </div>

      {/* Event Details Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t("Event Details")}
          </h2>
          <Link
            target="_blank"
            to={`/${selectedLanguage}/event-details/${eventData?.uid}`}
            className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-2"
          >
            {t("View Page")}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </Link>
        </div>

        <div className="space-y-3 text-gray-700">
          <div>
            <span className="font-semibold">{t("Dates")}: </span>
            <span className="text-sm font-medium">{formatDateRange()}</span>
          </div>
          <div>
            <span className="font-semibold">{t("Address")}: </span>
            <span className="text-sm font-medium">
              {eventData?.address?.name || "Address not specified"}
            </span>
          </div>
        </div>

        {/* Artists Section */}
        {eventData?.artistData?.length > 0 && (
          <div className="flex items-start mt-3">
            <h3 className="font-semibold">{t("Artists")}:</h3>
            <div className="flex flex-wrap gap-6">
              {eventData.artistData.map((artist, idx) => (
                <div key={idx} className="flex flex-col items-center space-y-3">
                  {/* Round Artist Image */}
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                    {artist?.info?.avatarUrl ? (
                      <Image
                        src={artist?.info?.avatarUrl}
                        alt={artist?.info?.displayName}
                        classes="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {initialsValue(artist?.info?.displayName)}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Artist Name */}
                  <div className="text-center">
                    <h4 className="font-semibold text-sm text-gray-900">
                      {artist?.info?.displayName || "Unknown Artist"}
                    </h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Organizer Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t("Organizer")}
          </h2>
          <Link
            target="_blank"
            to={`/${selectedLanguage}/organization/details/${organizationData?.uid}`}
            className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-2"
          >
            {t("View Page")}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </Link>
        </div>

        <div className="flex items-center space-x-4">
          {/* Organization Logo */}
          <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            {organizationData?.logo?.url ? (
              <Image
                src={organizationData.logo.url}
                alt={lngDetect(organizationData?.name)}
                classes="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-lg">
                {lngDetect(organizationData?.name)?.charAt(0) || "O"}
              </span>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {lngDetect(organizationData?.name) || "Organization Name"}
            </h3>
            <div className="flex items-center space-x-1 mt-1">
              {/* Star Rating */}
              <RatingStars
                rating={organizationData?.averageRating || 0}
                totalReviews={organizationData?.totalReview}
              />
              <span className="text-gray-500 text-sm font-medium ml-1">
                ({organizationData?.totalReview}{" "}
                {organizationData?.totalReview === 1 ? "Review" : "Reviews"})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Promoter Links Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t("Promoter Links")}
        </h2>
        <p className="text-sm font-medium text-gray-600 mb-6">
          {t(
            "Copy your promoter link and share it with your network so they can access the event"
          )}
          .
        </p>

        <div className="flex justify-center">
          <Button
            buttonLabel={t(
              copyStatus === "copied"
                ? "Copied promoter link!"
                : "Copy promoter link"
            )}
            buttonFunction={copyPromoterLink}
            disabled={copyStatus === "copied"}
            buttonClasses={`font-semibold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg ${
              copyStatus === "copied"
                ? "bg-green-500 text-white"
                : "bg-primary-gradient"
            }`}
          />
        </div>
      </div>

      {/* Commission Info (if available) */}
      {info?.settingsPerTicket && info.settingsPerTicket.length > 0 && (
        <div className="bg-gray-50 rounded-lg mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("Ticket Commission")}
          </h3>
          <div className="space-y-3">
            {info.settingsPerTicket.map((setting, index) => (
              <div key={index} className="flex flex-col md:flex-row justify-between md:items-center">
                <span className="font-medium uppercase">
                  {lngDetect(setting?.ticket?.name) || "Ticket"}
                </span>
                <div className="text-sm text-gray-600">
                  <span className="text-sm font-medium mr-4">
                    {t("Discount")}:{" "}
                    {setting?.discount?.type === "fixed" ? "$" : ""}
                    {setting?.discount?.type === "percentage"
                      ? setting?.discount?.value * 100
                      : setting?.discount?.value || 0}
                    {setting?.discount?.type === "percentage" ? "%" : ""}
                  </span>
                  <span className="text-sm font-medium">
                    {t("Commission")}:{" "}
                    {setting?.commission?.type === "fixed" ? "$" : ""}
                    {setting?.commission?.type === "percentage"
                      ? setting?.commission?.value * 100
                      : setting?.commission?.value || 0}
                    {setting?.commission?.type === "percentage" ? "%" : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoterDashboard;
